import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import debounce from "lodash/debounce";
import { fetchCustomerFiles } from "services/files-category.service";

// Tab identity is now the entry's `key` from `TABS_CONFIG` (see
// customer-container.jsx), not its position — the documents tab can move
// without this constant needing to change.
export const DOCUMENTS_TAB_KEY = "documents";

const NO_FILES = [];
const NOT_LOADED = { ownerKey: null, files: NO_FILES };

const useCustomerFiles = ({ companyId, customerId, selectedTab }) => {
  const [selectedCategoryFilters, setSelectedCategoryFilters] = useState([]);
  // The last list loaded of each kind: all of the customer's files, and the
  // files of the selected favourite categories. Each remembers which customer
  // it was loaded for and is shown only for that customer, so a list that was
  // never refreshed (another customer's, or none yet) can't be painted.
  const [allFiles, setAllFiles] = useState(NOT_LOADED);
  const [favouriteFiles, setFavouriteFiles] = useState(NOT_LOADED);
  const [isLoadingCustomerFiles, setIsLoadingCustomerFiles] = useState(false);
  const [isLoadingFilteredFiles, setIsLoadingFilteredFiles] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const currentOwnerKey = `${companyId || ""}|${customerId || ""}`;
  // Mirror the current owner key so async callbacks can read the latest value
  // and discard responses for a customer we've already navigated away from.
  const currentOwnerKeyRef = useRef(currentOwnerKey);
  useLayoutEffect(() => {
    currentOwnerKeyRef.current = currentOwnerKey;
  }, [currentOwnerKey]);
  // Every fetch gets a number; only the newest one may write its result. An
  // older request for the same customer (a filter or search the user has
  // already changed) can otherwise finish last and overwrite the list.
  const latestRequestRef = useRef(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchQuery(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchQuery(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const handleCategoryFilterChange = useCallback((categories) => {
    setSelectedCategoryFilters(categories);
  }, []);

  const fetchFilesForCurrentFilters = useCallback(async () => {
    if (!companyId || !customerId) {
      return false;
    }

    const requestOwnerKey = `${companyId}|${customerId}`;
    latestRequestRef.current += 1;
    const requestNumber = latestRequestRef.current;
    const isStale = () =>
      latestRequestRef.current !== requestNumber ||
      currentOwnerKeyRef.current !== requestOwnerKey;
    const hasCategoryFilters = selectedCategoryFilters.length > 0;
    const trimmedSearch =
      debouncedSearchQuery && debouncedSearchQuery.trim().length > 0
        ? debouncedSearchQuery.trim()
        : "";

    const filtersValue = hasCategoryFilters
      ? { categories: selectedCategoryFilters }
      : {};

    if (trimmedSearch) {
      filtersValue.textSearch = trimmedSearch;
    }

    const setList = hasCategoryFilters ? setFavouriteFiles : setAllFiles;
    if (hasCategoryFilters) {
      setIsLoadingFilteredFiles(true);
    } else {
      setIsLoadingCustomerFiles(true);
    }

    try {
      const response = await fetchCustomerFiles(
        companyId,
        customerId,
        filtersValue
      );
      // Discard the response if a newer request was made or we've navigated to
      // a different customer, so a stale in-flight request can't overwrite it.
      if (isStale()) {
        return false;
      }
      const files =
        response?.files || response?.filesCollection || response || [];
      setList({
        ownerKey: requestOwnerKey,
        files: Array.isArray(files) ? files : [],
      });
      return true;
    } catch (e) {
      if (isStale()) {
        return false;
      }
      // Record the attempt for this customer so the tab leaves the skeleton
      // (shows an empty list) instead of spinning forever after a failed fetch.
      setList({ ownerKey: requestOwnerKey, files: [] });
      return false;
    } finally {
      // Only the newest request clears the loading flags — both of them, since
      // an older request it replaced may have raised the other one.
      if (!isStale()) {
        setIsLoadingFilteredFiles(false);
        setIsLoadingCustomerFiles(false);
      }
    }
  }, [companyId, customerId, debouncedSearchQuery, selectedCategoryFilters]);

  // Remember the inputs we last fetched for, so that re-entering the documents
  // tab with unchanged inputs reuses the files already in state instead of
  // refetching (which flipped the loading flag and caused the visible flicker).
  const lastLoadedKeyRef = useRef(null);

  useEffect(() => {
    if (selectedTab !== DOCUMENTS_TAB_KEY) {
      return;
    }
    const loadKey = [
      companyId,
      customerId,
      (debouncedSearchQuery || "").trim(),
      selectedCategoryFilters.join(","),
    ].join("|");
    if (lastLoadedKeyRef.current === loadKey) {
      // Same customer + same search + same filters → data is already loaded.
      return;
    }
    fetchFilesForCurrentFilters().then((didLoad) => {
      if (didLoad) {
        lastLoadedKeyRef.current = loadKey;
      }
    });
  }, [
    selectedTab,
    companyId,
    customerId,
    debouncedSearchQuery,
    selectedCategoryFilters,
    fetchFilesForCurrentFilters,
  ]);

  const shownList =
    selectedCategoryFilters.length > 0 ? favouriteFiles : allFiles;
  const listIsForCurrentCustomer = shownList.ownerKey === currentOwnerKey;
  // Never expose another customer's files while between customers / loading.
  const filteredFiles = listIsForCurrentCustomer ? shownList.files : NO_FILES;

  const handleOnFileChange = useCallback(() => {
    if (selectedTab !== DOCUMENTS_TAB_KEY) {
      return;
    }
    fetchFilesForCurrentFilters();
  }, [selectedTab, fetchFilesForCurrentFilters]);

  // While the list to show doesn't belong to the current customer (between
  // customers, or before its first load), report loading so the documents tab
  // shows a skeleton rather than an empty list or the prior customer's files.
  const filesPending = !listIsForCurrentCustomer;

  return {
    searchQuery,
    handleSearchChange,
    selectedCategoryFilters,
    handleCategoryFilterChange,
    filteredFiles,
    isLoadingCustomerFiles: isLoadingCustomerFiles || filesPending,
    isLoadingFilteredFiles: isLoadingFilteredFiles || filesPending,
    handleOnFileChange,
  };
};

export default useCustomerFiles;
